import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { Modal } from "components/Modal/Modal";
import { ModalChannelsTabs } from "configs/types";
import { closeModal } from "store/modals/slice";

import ChannelsMainTab from "./tabs/ChannelsMainTab";
import ChannelsSettingsTab from "./tabs/ChannelsSettingsTab";
import { getChannelById } from "services/support/channelsService";
import { Channel } from "pages/Support/Channels/mockChannels";
import Button from "components/buttons/Button";
import { useTranslation } from "react-i18next";

const ChannelsModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const entityId = searchParams.get("entityId");

  const isEditMode = Boolean(entityId);

  const [channel, setChannel] = useState<Channel>({
    id: Date.now() % 10000,
    code: "",
    title: "",
    slug: "",
    type: "chat",
    description: "",
    isActive: true,
    defaultAssignee: "",
  });

  const [isLoading, setIsLoading] = useState(false);

  const tabs: ModalChannelsTabs[] = [
    ModalChannelsTabs.main,
    ModalChannelsTabs.settings,
  ];

  const [activeTab, setActiveTab] = useState<ModalChannelsTabs>(
    ModalChannelsTabs.main,
  );

  const handleCloseClick = () => {
    navigate(location.pathname);
    dispatch(closeModal());
  };

  const fetchChannelById = async (id: number) => {
    setIsLoading(true);
    try {
      const result = await getChannelById(id);
      if (!result) return;
      setChannel(result);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isEditMode || !entityId) return;
    fetchChannelById(Number(entityId));
  }, [isEditMode, entityId]);

  const handleSave = () => {
    if (!channel) return;
    handleCloseClick();
  };

  return (
    <Modal onClose={handleCloseClick}>
      <Modal.Header>{t("channel")}</Modal.Header>
      <Modal.Tabs
        tabs={tabs}
        activeTab={activeTab}
        onClick={(tab) => setActiveTab(tab)}
      />

      <Modal.Content>
        {activeTab === ModalChannelsTabs.main && (
          <ChannelsMainTab
            channel={channel}
            isEditMode={isEditMode}
            isLoading={isLoading}
            onChange={setChannel}
          />
        )}

        {activeTab === ModalChannelsTabs.settings && (
          <ChannelsSettingsTab entityId={entityId} />
        )}
      </Modal.Content>

      <Modal.Footer>
        <Button isSecondary txt={t("cancel")} handleClick={handleCloseClick} />
        <Button isPrimary handleClick={handleSave} txt={t("save")} />
      </Modal.Footer>
    </Modal>
  );
};

export default ChannelsModal;
